/**
 * generateChoices.js
 *
 * Generates multiple-choice options for Easy Mode.
 *
 * Logic:
 * 1. Start with the correct college
 * 2. Build pool from all unique colleges (from CSV), minus the correct one
 * 3. Randomly select 3 distractors, preferring ones used less often this game
 * 4. Combine correct + 3 distractors, shuffle
 *
 * All options come strictly from the parsed CSV dataset — no generated answers.
 */

import { createRNG } from './seedShuffle';
import { normalizeCollege } from './normalizeCollege';

/**
 * Generates 4 multiple-choice options (1 correct + 3 distractors).
 *
 * @param {string} correctCollege - the correct answer
 * @param {string[]} allColleges - all unique colleges from CSV
 * @param {Map<string, number>} usageTracker - tracks how many times each college
 *        has been used as a distractor in the current game, to spread usage
 * @param {number} questionSeed - per-question seed for deterministic distractor selection
 * @param {string[]} [altColleges] - other schools the player attended; excluded from the pool
 * @returns {string[]} - array of 4 college names, shuffled
 */
export function generateChoices(correctCollege, allColleges, usageTracker, questionSeed, altColleges = []) {
  const rng = createRNG(questionSeed);

  // Build distractor pool grouped by canonical school identity. This excludes any
  // alias of the correct answer (so "Mississippi" can never appear opposite
  // "Ole Miss") AND dedupes the pool by canonical, so two aliases of the same
  // school can never both become options, even if a future data entry
  // reintroduces a collision the data cleanup removed.
  // altColleges: other schools the player actually attended (transfers/juco) -
  // a school the player really went to can never be offered as a wrong answer.
  const correctCanon = normalizeCollege(correctCollege);
  const seenCanon = new Set([correctCanon, ...altColleges.map(normalizeCollege)]);
  const pool = [];
  for (const c of allColleges) {
    const canon = normalizeCollege(c);
    if (seenCanon.has(canon)) continue;
    seenCanon.add(canon);
    pool.push(c);
  }

  // Sort pool by usage count (ascending) so less-used colleges are preferred.
  // Within the same usage count, use the RNG to add randomness.
  const scored = pool.map((college) => ({
    college,
    usage: usageTracker.get(college) || 0,
    rand: rng(),
  }));

  scored.sort((a, b) => {
    if (a.usage !== b.usage) return a.usage - b.usage;
    return a.rand - b.rand;
  });

  // Pick the first 3 as distractors
  const distractors = scored.slice(0, 3).map((s) => s.college);

  // Track usage
  distractors.forEach((college) => {
    usageTracker.set(college, (usageTracker.get(college) || 0) + 1);
  });

// Place correct answer in a guaranteed random position
  const position = Math.floor(rng() * 4);
  const options = [...distractors];
  options.splice(position, 0, correctCollege);

  return options;
}
